import '@adornis/internationalization/client/language-picker/x-country-picker.js';
import type { XCountryPicker } from '@adornis/internationalization/client/language-picker/x-country-picker.js';
import { Language } from '@adornis/translation-core/languages.js';
import { html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';

// Thin Lit wrapper around the chemistry-styled <x-country-picker> so the
// generated-form renderer pattern (`{ value, onPicked, disabled, error }`)
// can drive it. The inner picker stores its value in an RXController, which
// can't be set via Lit's `.value=` property binding; we forward through the
// `next()` method on first mount and whenever the prop changes downstream.
//
// The picker's field is painted by the buchungsheld-theme chemistry input
// (<tr-formfield-wrapper>) on its inner `[background]` element, using a baked-in grey that
// clashes with the DaisyUI `.input`s around it. The colour isn't a CSS variable and doesn't
// inherit across the nested shadow boundaries, so no ancestor-level override reaches it.
// Instead `styleFieldBackgrounds` walks the open shadow roots, finds every
// <tr-formfield-wrapper>, and injects a stylesheet that restyles `[background]` to match a
// DaisyUI `.input` (base-100 fill + input border) in every state. The chemistry dropdown
// lazily remounts the field wrapper when it opens, closes, or a value is picked, so we
// re-apply on those interactions (across a few frames, since the new wrapper's shadow root
// isn't there immediately). The dropdown panel/list are separate elements, so their grey
// highlights are left intact.
@customElement('mw-country-picker')
export class MwCountryPicker extends LitElement {
  @property({ attribute: false }) value: string | null = null;
  @property({ type: Boolean }) disabled = false;
  @property() error = '';

  private pickerRef = createRef<XCountryPicker>();
  private injectedSheets = new WeakMap<ShadowRoot, CSSStyleSheet>();

  override createRenderRoot() {
    return this;
  }

  override firstUpdated() {
    const picker = this.pickerRef.value;
    if (!picker) return;
    if (this.value) picker.value.next(this.value);
    void picker.updateComplete.then(() => this.restyleSoon(picker));
    // open → click, close → focusout, select → value-picked: each remounts the field wrapper.
    for (const event of ['click', 'focusout', 'value-picked']) {
      picker.addEventListener(event, () => this.restyleSoon(picker));
    }
  }

  override updated(changed: Map<string, unknown>) {
    const picker = this.pickerRef.value;
    if (!picker) return;
    if (changed.has('value') && picker.value.value !== this.value) picker.value.next(this.value);
    this.restyleSoon(picker);
  }

  // Re-apply across a few frames: a freshly remounted wrapper's shadow root may not exist on
  // the first pass. styleFieldBackgrounds is idempotent, so the extra passes are cheap no-ops.
  private restyleSoon(picker: XCountryPicker) {
    for (const ms of [0, 120, 350, 800]) setTimeout(() => this.styleFieldBackgrounds(picker), ms);
  }

  // Find every <tr-formfield-wrapper> under the picker (crossing the open shadow roots) and
  // inject a sheet that restyles its `[background]` element to match a DaisyUI `.input`
  // (base-100 fill + input border) in every state. Matching the theme's own [background]
  // selectors (resting/:hover/:focus-within) and appending after its sheet wins the cascade.
  // Self-healing: the sheet is reused per root and re-added if a re-render dropped it.
  private styleFieldBackgrounds(picker: XCountryPicker) {
    const css =
      ':host [background],:host(:not([disabled])) [background]:hover,:host [background]:focus-within{' +
      'background:var(--color-base-100,oklch(98% 0.001 106.423))!important;' +
      'border:var(--border,1px) solid var(--input-color,color-mix(in oklab,var(--color-base-content,#1c1917) 20%,#0000))!important;' +
      '}';
    const visit = (el: Element) => {
      const sr = (el as Element & { shadowRoot?: ShadowRoot | null }).shadowRoot;
      if (el.tagName.toLowerCase() === 'tr-formfield-wrapper' && sr) {
        let sheet = this.injectedSheets.get(sr);
        if (!sheet) {
          sheet = new CSSStyleSheet();
          sheet.replaceSync(css);
          this.injectedSheets.set(sr, sheet);
        }
        if (!sr.adoptedStyleSheets.includes(sheet)) sr.adoptedStyleSheets = [...sr.adoptedStyleSheets, sheet];
      }
      for (const c of Array.from(el.children)) visit(c);
      if (sr) for (const c of Array.from(sr.children)) visit(c);
    };
    visit(picker);
  }

  override render() {
    return html`
      <x-country-picker
        ${ref(this.pickerRef)}
        showflag
        ?disabled=${this.disabled}
        language=${Language.GERMAN}
        @value-picked=${(e: CustomEvent) =>
          this.dispatchEvent(
            new CustomEvent('value-picked', {
              detail: e.detail,
              bubbles: true,
              composed: true,
            }),
          )}
      ></x-country-picker>
      ${this.error ? html`<div class="text-error text-xs mt-1">${this.error}</div>` : nothing}
    `;
  }
}
